// Per-audience abstract pattern — geometry only, no figurative SVG.
// Ported from the accepted design's quiz.jsx AudiencePattern.

const common = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 0.8,
} as const;

export function AudiencePattern({ id }: { id: string }) {
  switch (id) {
    case "kids":
      return (
        <svg viewBox="0 0 100 100">
          {Array.from({ length: 7 }).map((_, i) => (
            <circle key={i} cx={20 + i * 10} cy={30 + (i % 2) * 40} r={3 + i} {...common} />
          ))}
        </svg>
      );
    case "teens":
      return (
        <svg viewBox="0 0 100 100">
          {Array.from({ length: 20 }).map((_, i) => (
            <line key={i} x1={5 + i * 4.5} y1={5} x2={95 - i * 4.5} y2={95} {...common} />
          ))}
        </svg>
      );
    case "women":
      return (
        <svg viewBox="0 0 100 100">
          {Array.from({ length: 8 }).map((_, i) => (
            <circle key={i} cx={50} cy={50} r={10 + i * 5} {...common} />
          ))}
        </svg>
      );
    case "men":
      return (
        <svg viewBox="0 0 100 100">
          {Array.from({ length: 5 }).map((_, i) => (
            <rect
              key={i}
              x={10 + i * 6}
              y={10 + i * 6}
              width={80 - i * 12}
              height={80 - i * 12}
              {...common}
            />
          ))}
        </svg>
      );
    case "skeptic":
      return (
        <svg viewBox="0 0 100 100">
          <text x={20} y={65} fontSize={60} fontFamily="serif" fill="currentColor" opacity={0.4}>
            ?
          </text>
          {Array.from({ length: 8 }).map((_, i) => (
            <line key={i} x1={0} y1={10 + i * 12} x2={100} y2={10 + i * 12} {...common} />
          ))}
        </svg>
      );
    case "politician":
      return (
        <svg viewBox="0 0 100 100">
          {Array.from({ length: 6 }).map((_, i) => (
            <rect
              key={i}
              x={10 + i * 14}
              y={100 - (i + 1) * 15}
              width={10}
              height={(i + 1) * 15}
              {...common}
            />
          ))}
        </svg>
      );
    case "farmer":
      return (
        <svg viewBox="0 0 100 100">
          {Array.from({ length: 10 }).flatMap((_, r) =>
            Array.from({ length: 10 }).map((_, c) => (
              <rect key={`${r}-${c}`} x={r * 10} y={c * 10} width={10} height={10} {...common} />
            )),
          )}
        </svg>
      );
    case "butcher":
      return (
        <svg viewBox="0 0 100 100">
          {Array.from({ length: 6 }).map((_, i) => (
            <g key={i}>
              <line x1={10 + i * 14} y1={10} x2={10 + i * 14} y2={90} {...common} />
              <line x1={10} y1={10 + i * 14} x2={90} y2={10 + i * 14} {...common} />
            </g>
          ))}
        </svg>
      );
    case "hunter":
      return (
        <svg viewBox="0 0 100 100">
          <circle cx={50} cy={50} r={35} {...common} />
          <circle cx={50} cy={50} r={25} {...common} />
          <circle cx={50} cy={50} r={15} {...common} />
          <circle cx={50} cy={50} r={5} fill="currentColor" />
          <line x1={50} y1={5} x2={50} y2={95} {...common} />
          <line x1={5} y1={50} x2={95} y2={50} {...common} />
        </svg>
      );
    case "activist":
    default:
      return (
        <svg viewBox="0 0 100 100">
          {Array.from({ length: 12 }).map((_, i) => (
            <path
              key={i}
              d={`M 50 50 L ${50 + Math.cos((i * Math.PI) / 6) * 40} ${
                50 + Math.sin((i * Math.PI) / 6) * 40
              }`}
              {...common}
            />
          ))}
        </svg>
      );
  }
}

export function DotField() {
  const rows = 28;
  const cols = 48;
  const items: { r: number; c: number; type: "x" | "dot" }[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const ix = (r * 7 + c * 13) % 17;
      if (ix > 14) items.push({ r, c, type: ix === 15 ? "x" : "dot" });
    }
  }
  return (
    <svg
      className="dotfield"
      viewBox={`0 0 ${cols * 10} ${rows * 10}`}
      preserveAspectRatio="xMidYMid slice"
    >
      {items.map((it, i) => {
        const x = it.c * 10 + 5;
        const y = it.r * 10 + 5;
        if (it.type === "x") {
          return (
            <g key={i} stroke="currentColor" strokeWidth={0.5}>
              <line x1={x - 2} y1={y - 2} x2={x + 2} y2={y + 2} />
              <line x1={x - 2} y1={y + 2} x2={x + 2} y2={y - 2} />
            </g>
          );
        }
        return <circle key={i} cx={x} cy={y} r={0.6} fill="currentColor" />;
      })}
    </svg>
  );
}
